import apiClient from './api';
import { getApiAccessToken } from './UserService';

/**
 * Email notification configuration for an address system
 */
export interface EmailNotificationConfig {
    id: number;
    addressSystemId: number;
    addressSystemName?: string;
    
    // Object identification fields
    includeObjectName: boolean;
    includeAddressSystem: boolean;
    includeStatus: boolean;
    
    // Contract and location fields
    includeContractName: boolean;
    includeStreetName: boolean;
    includeOrderNumber: boolean;
    includeSiteNumber: boolean;
    includeNetworkNumber: boolean;
    
    // Date fields
    includePlannedDate: boolean;
    includeStartDate: boolean;
    includeReadyDate: boolean;
    includeApprovedDate: boolean;
    includeModifiedDate: boolean;
    
    // Personnel fields
    includeForeman: boolean;
    includeContractorForeman: boolean;
    includeProjectManager: boolean;
    includeSubscriber: boolean;
    
    // Additional fields
    includeDescription: boolean;
    includeObjectLength: boolean;
    includeType: boolean;
}

/**
 * Request to update email notification configuration
 */
export interface UpdateEmailNotificationConfigRequest {
    addressSystemId: number;
    
    includeObjectName: boolean;
    includeAddressSystem: boolean;
    includeStatus: boolean;
    includeContractName: boolean;
    includeStreetName: boolean;
    includeOrderNumber: boolean;
    includeSiteNumber: boolean;
    includeNetworkNumber: boolean;
    includePlannedDate: boolean;
    includeStartDate: boolean;
    includeReadyDate: boolean;
    includeApprovedDate: boolean;
    includeModifiedDate: boolean;
    includeForeman: boolean;
    includeContractorForeman: boolean;
    includeProjectManager: boolean;
    includeSubscriber: boolean;
    includeDescription: boolean;
    includeObjectLength: boolean;
    includeType: boolean;
    
    modifiedByUserId?: number;
}

/**
 * Field configuration item for display
 */
export interface EmailFieldConfig {
    key: keyof EmailNotificationConfig;
    label: string;
    textCode: string;
    category: 'identification' | 'location' | 'dates' | 'personnel' | 'additional';
}

/**
 * Available email notification fields with their display labels
 */
export const EMAIL_FIELD_CONFIGS: EmailFieldConfig[] = [
    // Object identification
    { key: 'includeObjectName', label: 'Object Name', textCode: 'LABEL_EMAIL_FIELD_OBJECT_NAME', category: 'identification' },
    { key: 'includeAddressSystem', label: 'Address System', textCode: 'LABEL_EMAIL_FIELD_ADDRESS_SYSTEM', category: 'identification' },
    { key: 'includeStatus', label: 'Status', textCode: 'LABEL_EMAIL_FIELD_STATUS', category: 'identification' },
    { key: 'includeType', label: 'Type', textCode: 'LABEL_EMAIL_FIELD_TYPE', category: 'identification' },
    
    // Contract and location
    { key: 'includeContractName', label: 'Contract Name', textCode: 'LABEL_EMAIL_FIELD_CONTRACT_NAME', category: 'location' },
    { key: 'includeStreetName', label: 'Street Name', textCode: 'LABEL_EMAIL_FIELD_STREET_NAME', category: 'location' },
    { key: 'includeOrderNumber', label: 'Order Number', textCode: 'LABEL_EMAIL_FIELD_ORDER_NUMBER', category: 'location' },
    { key: 'includeSiteNumber', label: 'Site Number', textCode: 'LABEL_EMAIL_FIELD_SITE_NUMBER', category: 'location' },
    { key: 'includeNetworkNumber', label: 'Network Number', textCode: 'LABEL_EMAIL_FIELD_NETWORK_NUMBER', category: 'location' },
    
    // Dates
    { key: 'includePlannedDate', label: 'Planned Date', textCode: 'LABEL_EMAIL_FIELD_PLANNED_DATE', category: 'dates' },
    { key: 'includeStartDate', label: 'Start Date', textCode: 'LABEL_EMAIL_FIELD_START_DATE', category: 'dates' },
    { key: 'includeReadyDate', label: 'Ready Date', textCode: 'LABEL_EMAIL_FIELD_READY_DATE', category: 'dates' },
    { key: 'includeApprovedDate', label: 'Approved Date', textCode: 'LABEL_EMAIL_FIELD_APPROVED_DATE', category: 'dates' },
    { key: 'includeModifiedDate', label: 'Modified Date', textCode: 'LABEL_EMAIL_FIELD_MODIFIED_DATE', category: 'dates' },
    
    // Personnel
    { key: 'includeForeman', label: 'Foreman', textCode: 'LABEL_EMAIL_FIELD_FOREMAN', category: 'personnel' },
    { key: 'includeContractorForeman', label: 'Contractor Foreman', textCode: 'LABEL_EMAIL_FIELD_CONTRACTOR_FOREMAN', category: 'personnel' },
    { key: 'includeProjectManager', label: 'Project Manager', textCode: 'LABEL_EMAIL_FIELD_PROJECT_MANAGER', category: 'personnel' },
    { key: 'includeSubscriber', label: 'Subscriber', textCode: 'LABEL_EMAIL_FIELD_SUBSCRIBER', category: 'personnel' },
    
    // Additional
    { key: 'includeDescription', label: 'Description', textCode: 'LABEL_EMAIL_FIELD_DESCRIPTION', category: 'additional' },
    { key: 'includeObjectLength', label: 'Object Length', textCode: 'LABEL_EMAIL_FIELD_OBJECT_LENGTH', category: 'additional' },
];

/**
 * Get all email notification configurations for all address systems
 */
export const getAllEmailNotificationConfigs = async (): Promise<EmailNotificationConfig[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get<EmailNotificationConfig[]>('/api/EmailNotificationConfig', {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return (response.data || []).map(c => ({
        ...c,
        addressSystemName: c.addressSystemName?.trim()
    }));
};

/**
 * Get email notification configuration for a specific address system
 */
export const getEmailNotificationConfig = async (addressSystemId: number): Promise<EmailNotificationConfig> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get<EmailNotificationConfig>(`/api/EmailNotificationConfig/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};

/**
 * Save (create or update) email notification configuration
 */
export const saveEmailNotificationConfig = async (
    config: UpdateEmailNotificationConfigRequest
): Promise<EmailNotificationConfig> => {
    const token = await getApiAccessToken();
    const response = await apiClient.post<EmailNotificationConfig>('/api/EmailNotificationConfig', config, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};

/**
 * Update email notification configuration for a specific address system
 */
export const updateEmailNotificationConfig = async (
    addressSystemId: number,
    config: UpdateEmailNotificationConfigRequest
): Promise<EmailNotificationConfig> => {
    const token = await getApiAccessToken();
    const response = await apiClient.put<EmailNotificationConfig>(
        `/api/EmailNotificationConfig/${addressSystemId}`,
        config,
        {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }
    );
    return response.data;
};
